import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter/index';
import {useDispatch, useSelector} from 'react-redux';
import {ERROR_CODES} from 'src/components/authentication/errors';
import {CONSTANTS} from 'src/components/authentication/contants';
import {
  userSignin,
  USER_SIGNIN_ERROR,
} from 'src/action-creators/referrer-portal/authentication';
import {validateEmail} from 'src/utils/index';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';

import {
  Input,
  TitleMedium,
  BodyMedium,
  ButtonTextMedium,
  Button,
  InContextAlert,
} from '@spaced-out/ui-design-system/lib/components/index';
import GoogleIcon from 'src/images/google-icon.svg';
import css from './sign-in.css';


const SignInPage = ({
  setCreds,
}: {
  setCreds: (arg1: {email: string; password: string}) => unknown;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {t, makeKey} = useLabelTranslation();

  const signinError = useSelector((state) => state.referrerPortal.signin_error);
  const {location} = history;
  const {
    query: {email: defualtEmail = ''},
  } = location;

  const [email, setEmail] = React.useState<string>(
    decodeURIComponent(defualtEmail),
  );
  const [password, setPassword] = React.useState<string>('');
  const [touched, setTouched] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [signingIn, setSigningIn] = React.useState<boolean>(false);

  const resetSignInError = () =>
    dispatch({type: USER_SIGNIN_ERROR, payload: null});

  const handleResetPassowrd = () => {
    resetSignInError();
    history.replace(`/forgot`);
  };

  const handleSignUp = () => {
    resetSignInError();
    history.replace({
      ...history.location,
      pathname: `/signup`,
      query: location.query,
    });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePassowrdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const isError = !email || !password || !validateEmail(email);

  const handleSubmit = () => {
    if (isError) {
      return;
    }
    setSigningIn(true);
    dispatch(userSignin({email, password})).then((response) => {
      //@ts-expect-error
      if (response) {
        setCreds({email, password});
      } else {
        setSigningIn(false);
      }
    });
  };

  const handleGoogleSignin = () => {
    dispatch(reduxApi.get('auth/login/google')).then((googleAuthUrl) =>
      window.open(googleAuthUrl, '_self'),
    );
  };

  return (
    <div className={css.signInPage}>
      <div className={css.signInSection}>
        <TitleMedium className={css.contentTitle}>{`${t(
          'Hi_welcome_to_Sense_Referrals',
          'Hi, welcome to Sense Referrals',
        )}!`}</TitleMedium>
        <div className={css.credInput}>
          <Button
            onClick={handleGoogleSignin}
            classNames={{wrapper: css.googleBtn}}
            type="tertiary"
          >
            <div className={css.googleBtnContent}>
              <GoogleIcon />
              <ButtonTextMedium>
                {t('Sign_in_with_Google', 'Sign in with Google')}
              </ButtonTextMedium>
            </div>
          </Button>
        </div>
        <div className={css.credInput}>
          <div className={css.subHeaderContainer}>
            <div className={css.subHeaderBorder} />
            <BodyMedium className={css.subHeaderText} color="secondary">
              {t(
                'or_use_your_email_to_sign_in',
                'or use your email to sign in',
              )}
            </BodyMedium>
            <div className={css.subHeaderBorder} />
          </div>
        </div>
        <form onSubmit={handleSubmit} className={css.credForm}>
          <div className={css.credSection}>
            <div className={css.credInput}>
              <Input
                label={t('Email', 'Email')}
                placeholder={t('Enter_email', 'Enter email')}
                size="medium"
                type="text"
                value={email}
                error={touched['email'] && (!email || !validateEmail(email))}
                errorText={t(
                  makeKey(CONSTANTS.INVALID_EMAIL),
                  CONSTANTS.INVALID_EMAIL,
                )}
                onBlur={() => setTouched({...touched, ['email']: true})}
                onChange={handleEmailChange}
                disabled={!!defualtEmail}
              />
            </div>
            <div className={css.credInput}>
              <Input
                label={t('Password', 'Password')}
                placeholder={t('Enter_password', 'Enter password')}
                size="medium"
                type="password"
                error={touched['password'] && !password}
                onBlur={() => setTouched({...touched, ['password']: true})}
                errorText={t(
                  makeKey(CONSTANTS.EMPTY_PASSWORD),
                  CONSTANTS.EMPTY_PASSWORD,
                )}
                value={password}
                onChange={handlePassowrdChange}
              />
            </div>
          </div>
          <Button
            size="medium"
            type="primary"
            classNames={{wrapper: css.signInBtn}}
            onClick={handleSubmit}
            disabled={signingIn || isError}
          >
            {signingIn
              ? t('Signing_In', 'Signing In')
              : t('Sign_In', 'Sign In')}
          </Button>
        </form>
        {signinError && (
          <InContextAlert
            semantic="danger"
            classNames={{wrapper: css.loginError}}
          >
            {/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'. */}
            {ERROR_CODES[signinError] || signinError}
          </InContextAlert>
        )}
        <div className={css.resetPassword}>
          <div>
            <BodyMedium className={css.forgotLoginText}>
              {t('Can\'t_log_in', 'Can\'t log in')}?
            </BodyMedium>

            <ButtonTextMedium
              color="clickable"
              className={css.resetLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={handleResetPassowrd}
            >
              {t('Reset_password', 'Reset password')}
            </ButtonTextMedium>
          </div>
          <div>
            <BodyMedium className={css.forgotLoginText}>{`${t(
              'New_user',
              'New user',
            )}?`}</BodyMedium>

            <ButtonTextMedium
              color="clickable"
              className={css.resetLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={handleSignUp}
            >
              {t('Sign_up_here', 'Sign up here')}
            </ButtonTextMedium>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
